import React, { FC, useState, useRef } from 'react';
import clsx from 'clsx';
import { useMutation, useQuery } from '@apollo/client';
import * as AuthQuery from 'graphql/auth.graphql';
import * as PaymentQuery from 'graphql/payment.graphql';
import useUserSession from 'hooks/useUserSession';
import usePermittedSessionPreferences from 'hooks/usePermittedSessionPreferences';
import useUpdateSessionPreferences, { InputSessionPreferences } from 'hooks/useUpdateSessionPreferences';
import Alert from 'components/Alert';
import CashierNameModal from './CashierNameModal';
import IconButton from 'components/Button/IconButton';
import Message from 'components/Message';
import Modal from 'components/Modal';
import SideModal from 'components/SideModal';
import SideModalHeader from 'components/SideModal/Header';
import ToggleSwitch from 'components/ToggleSwitch';
import { Terminal } from 'api/data/payment/types';
import { Session } from 'api/data/user/types';
import './style.scss';

type InPersonPaymentsProps = {
  spaceSlug: string;
  provider?: string;
};

type InPersonPaymentsModalProps = {
  session: Session;
  connectedPaymentTerminal: Terminal | null;
} & InPersonPaymentsProps;

type TerminalModal = {
  title: string;
  description: (terminalName1: string, terminalName2: string, cashierName: string) => void;
  icon: string;
  confirmLabel: string;
};

const terminalModal: Record<string, TerminalModal> = {
  switch: {
    title: 'Switch readers',
    description: (
      terminalName1: string,
      terminalName2: string,
    ) => `This device can only be connected to one reader at a time.

    Are you sure you want to disconnect from “${terminalName1}” and connect to “${terminalName2}”?
    `,
    icon: 'square_info',
    confirmLabel: 'Connect',
  },
  disconnect: {
    title: 'Disconnect from Reader',
    description: (terminalName, _, cashierName) =>
      `Are you sure you want to disconnect ${cashierName} from  “${terminalName}”?`,
    icon: 'warning',
    confirmLabel: 'Disconnect',
  },
};

const InPersonPayments: FC<InPersonPaymentsProps> = ({ spaceSlug, provider }) => {
  const { data: userSession } = useUserSession();
  const { hasInPersonPaymentsPermission, connectedPaymentTerminal } = usePermittedSessionPreferences(spaceSlug);

  if (!userSession || !hasInPersonPaymentsPermission) return null;

  return (
    <InPersonPaymentsModal
      spaceSlug={spaceSlug}
      session={userSession.session}
      provider={provider}
      connectedPaymentTerminal={connectedPaymentTerminal}
    />
  );
};

const InPersonPaymentsModal: FC<InPersonPaymentsModalProps> = ({ session, spaceSlug, connectedPaymentTerminal }) => {
  const cashierModalRef = useRef<HTMLDivElement>(null);
  const terminalModalRef = useRef<HTMLDivElement>(null);
  const terminalQuery = {
    query: PaymentQuery.PaymentTerminals,
    variables: { currentSpace: spaceSlug },
  };
  const [isOpenInPersonModal, setIsOpenInPersonModal] = useState(false);
  const [isOpenCashierModal, setIsOpenCashierModal] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState<Terminal | null>();
  const [showTerminalModal, setShowOpenTerminalModal] = useState({ type: 'switch', isOpen: false });
  const [setSessionTerminal, { loading: sessionTerminalLoading, error: sessionTerminalError }] = useMutation(
    AuthQuery.SetSessionTerminal,
    {
      refetchQueries: [terminalQuery, { query: AuthQuery.Session }],
    },
  );

  const { data: dataTerminals } = useQuery<{ paymentTerminals: Terminal[] }>(PaymentQuery.PaymentTerminals, {
    variables: { currentSpace: spaceSlug },
  });

  const updateSessionPreferences = useUpdateSessionPreferences(spaceSlug || '');

  const { sessionPreferences } = session;

  const terminalHasActiveSession = !!selectedTerminal?.activeSession;
  const defaultName = sessionPreferences?.cashierName || session.currentUser?.fullName || '';

  const saveSessionPreferences = async (input: InputSessionPreferences) => {
    await updateSessionPreferences(input);
  };

  const handleOnChange = async (field: string, value: boolean | string) => {
    const input = {
      [field]: value,
    };

    if (field === 'acceptInPersonPayments') {
      input['acceptCashAndCheckPayments'] = value;

      if (value) {
        setIsOpenCashierModal(true);
        input['cashierName'] = defaultName;
      }
    }
    await saveSessionPreferences(input);
  };

  const connectOrDisconnectClicked = (terminal: Terminal) => {
    setSelectedTerminal(terminal);

    const currentUserTerminal = terminal?.id === connectedPaymentTerminal?.id;
    if (terminal.activeSession || currentUserTerminal) {
      return setShowOpenTerminalModal({ type: 'disconnect', isOpen: true });
    }

    if (connectedPaymentTerminal) {
      return setShowOpenTerminalModal({ type: 'switch', isOpen: true });
    }

    if (sessionPreferences.cashierName) {
      void connectOrDisconnectSelectedTerminal(terminal);
    } else {
      setIsOpenCashierModal(true);
    }
  };

  const saveSessionPreferencesIfNeeded = async (action: string) => {
    if (action === 'CONNECT' && !sessionPreferences.acceptInPersonPayments && sessionPreferences.cashierName) {
      await saveSessionPreferences({
        acceptInPersonPayments: true,
        acceptCashAndCheckPayments: true,
      });
    }
  };

  const connectOrDisconnectSelectedTerminal = async (terminal?: Terminal | null) => {
    const target = terminal ?? selectedTerminal;

    const isCurrentUserTerminal = target?.id === connectedPaymentTerminal?.id;
    const isOtherUserActive = !!target?.activeSession;

    const action = isCurrentUserTerminal || isOtherUserActive ? 'DISCONNECT' : 'CONNECT';

    await saveSessionPreferencesIfNeeded(action);

    try {
      await setSessionTerminal({
        variables: {
          terminalId: target?.id,
          action,
          currentSpace: spaceSlug,
        },
      });
    } catch (err) {
      setShowMessage(true);
      return err;
    }

    setShowOpenTerminalModal({ ...showTerminalModal, isOpen: false });
    setSelectedTerminal(undefined);

    if (action === 'CONNECT') {
      setShowMessage(true);
    }
  };

  const saveCashierNameAndConnectIfNeeded = async (cashierName: string) => {
    await saveSessionPreferences({
      cashierName,
      acceptInPersonPayments: true,
    });

    if (selectedTerminal) void connectOrDisconnectSelectedTerminal();
  };

  return (
    <div className="in-person-payments">
      <IconButton icon="credit_card" className="button-size-m" onClick={() => setIsOpenInPersonModal(true)}>
        In Person Payments
      </IconButton>
      <SideModal
        isOpen={isOpenInPersonModal}
        toggle={setIsOpenInPersonModal}
        allowedShards={[cashierModalRef, terminalModalRef]}
        header={<SideModalHeader title="In Person Payments" onClose={() => setIsOpenInPersonModal(false)} />}>
        <div className="in-person-modal">
          <Message
            type={sessionTerminalError?.message ? 'failed' : 'succeeded'}
            showMessage={(!!connectedPaymentTerminal || !!sessionTerminalError) && showMessage}
            setShowMessage={setShowMessage}>
            {sessionTerminalError?.message
              ? sessionTerminalError.message
              : 'Device ' + connectedPaymentTerminal?.name + ' connected!'}
          </Message>
          <div className="box accept-in-person-box">
            <ToggleSwitch
              label="Accept in person payments"
              toggleValue={sessionPreferences.acceptInPersonPayments}
              handleOnChange={() =>
                void handleOnChange('acceptInPersonPayments', !sessionPreferences.acceptInPersonPayments)
              }
            />
            {sessionPreferences.acceptInPersonPayments && (
              <div className="in-person-wrapper">
                <div className="horizontal-line"></div>
                <div className="row space-between cashier-wrapper">
                  <span className="size-xxs subtitle-darker label">Cashier</span>
                  <IconButton
                    aria-label="Edit cashier name"
                    icon="edit"
                    className="button-size-m "
                    onClick={() => setIsOpenCashierModal(true)}
                    iconSide="RIGHT">
                    {sessionPreferences.cashierName || session.currentUser?.fullName}
                  </IconButton>
                </div>
                <div className="box">
                  {!!connectedPaymentTerminal && (
                    <div className="row space-between align-center connected-terminal">
                      <span className="subtitle-x-small">{connectedPaymentTerminal.name}</span>
                      <button
                        className="button-size-ml font-size-xxxs button-outline"
                        onClick={() => connectOrDisconnectClicked(connectedPaymentTerminal)}>
                        Disconnect
                      </button>
                    </div>
                  )}
                  <ToggleSwitch
                    label="Cash and check"
                    toggleValue={sessionPreferences.acceptCashAndCheckPayments}
                    handleOnChange={() =>
                      void handleOnChange('acceptCashAndCheckPayments', !sessionPreferences.acceptCashAndCheckPayments)
                    }
                  />
                </div>
                {!!dataTerminals?.paymentTerminals?.length && (
                  <Alert
                    type="informative"
                    icon="warning_circle_solid"
                    content="Select a reader below to accept card payments"
                  />
                )}
              </div>
            )}
          </div>
          {!!dataTerminals?.paymentTerminals && dataTerminals?.paymentTerminals.length > 0 && (
            <>
              <p className="subtitle-x-small neutral">Card Readers</p>
              <div className="box card-terminals-list">
                {dataTerminals?.paymentTerminals.map(terminal => {
                  if (connectedPaymentTerminal?.id === terminal.id && sessionPreferences.acceptInPersonPayments)
                    return null;

                  return (
                    <div key={terminal.id} className="row space-between align-center">
                      <div className="row direction-column">
                        <span className="subtitle-x-small">{terminal.name}</span>
                        <span className="subtitle-xx-small neutral">{terminal.activeSession?.cashierName}</span>
                      </div>
                      <button
                        className={clsx('button-size-ml font-size-xxxs', {
                          'button-outline': !!terminal.activeSession,
                        })}
                        onClick={() => connectOrDisconnectClicked(terminal)}
                        disabled={sessionTerminalLoading}>
                        {sessionTerminalLoading ? 'Loading...' : terminal.activeSession ? 'Disconnect' : 'Connect'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </SideModal>
      <CashierNameModal
        modalRef={cashierModalRef}
        setIsOpen={setIsOpenCashierModal}
        isOpen={isOpenCashierModal}
        handleSaveCashierName={cashierName => saveCashierNameAndConnectIfNeeded(cashierName)}
        defaultName={defaultName}
      />
      <Modal
        handleOnCancel={() => setShowOpenTerminalModal({ ...showTerminalModal, isOpen: false })}
        handleOnConfirm={() => void connectOrDisconnectSelectedTerminal()}
        header={terminalModal[showTerminalModal.type].title}
        confirmlabel={terminalModal[showTerminalModal.type].confirmLabel}
        denyLabel="Cancel"
        visible={showTerminalModal.isOpen}
        headerIcon={terminalModal[showTerminalModal.type].icon}
        modalRef={terminalModalRef}
        className="terminal-confirmation">
        {terminalModal[showTerminalModal.type].description(
          terminalHasActiveSession ? selectedTerminal?.name : connectedPaymentTerminal?.name || '',
          selectedTerminal?.name || '',
          selectedTerminal?.activeSession?.cashierName || '',
        )}
      </Modal>
    </div>
  );
};

export default InPersonPayments;
